import { yupResolver } from '@hookform/resolvers/yup'
import { getData } from 'country-list'
import { map } from 'lodash-es'
import { FC, useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

import SelectField from '@/components/select-field'
import { Button, Input } from '@/components/ui'
import { FinancialSettingsInvoiceConfig } from '../../../../common/models/settings'
import FormGroup from '../../../../core/components/FormGroup'
import { useCheckPermission } from '../../../../core/hooks/use-check-permission'
import {
  getFinancialSettingsInvoiceConfig,
  updateFinancialSettingsInvoiceConfig,
} from '../../../../core/services/festival-settings'
import { MessageBox } from '../../messages/MessageBox'

interface IFinancingValues {
  generateInvoice: 'OFF' | 'ON_DEMAND_MANUAL' | 'ON_DEMAND_AUTO' | 'ALWAYS'
  companyName: string
  companyIdNumber?: string
  vatCode?: string
  country: string
  city: string
  companyAddress: string
}

const GENERATE_INVOICE_OPTIONS = [
  { value: 'OFF', label: 'Off' },
  { value: 'ON_DEMAND_MANUAL', label: 'On Demand Manual' },
  { value: 'ON_DEMAND_AUTO', label: 'On Demand Auto' },
  { value: 'ALWAYS', label: 'Always' },
]

const schema = yup.object().shape({
  generateInvoice: yup
    .string()
    .oneOf(['OFF', 'ON_DEMAND_MANUAL', 'ON_DEMAND_AUTO', 'ALWAYS'])
    .required(),
  companyName: yup.string().required(),
  companyIdNumber: yup.string().optional(),
  vatCode: yup.string().optional(),
  country: yup.string().required(),
  city: yup.string().required(),
  companyAddress: yup.string().required(),
})

export const FinancingSettingsSection: FC = () => {
  const [isError, setIsError] = useState(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })

  const [financialSettings, setFinancialSettings] =
    useState<FinancialSettingsInvoiceConfig>()

  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<IFinancingValues>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  })

  const generateInvoiceValue = watch('generateInvoice')
  const countryValue = watch('country')

  const getDataFinancialConfig = useCallback(async () => {
    try {
      const { data } = await getFinancialSettingsInvoiceConfig()
      reset({
        city: data.city,
        companyAddress: data.address,
        companyIdNumber: data.companyIdNumber,
        companyName: data.companyName,
        country: data.country,
        generateInvoice: data.invoiceGenerationType as
          | 'OFF'
          | 'ON_DEMAND_MANUAL'
          | 'ON_DEMAND_AUTO'
          | 'ALWAYS',
        vatCode: data.vatNumber,
      })
      setFinancialSettings(data)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      }
    }
  }, [reset])

  useEffect(() => {
    getDataFinancialConfig()
  }, [getDataFinancialConfig])

  const onSubmit = useCallback(async (values: IFinancingValues) => {
    setIsSaving(true)
    try {
      const { status } = await updateFinancialSettingsInvoiceConfig({
        address: values.companyAddress,
        city: values.city,
        companyIdNumber: values.companyIdNumber,
        companyName: values.companyName,
        country: values.country,
        invoiceGenerationType:
          values.generateInvoice as FinancialSettingsInvoiceConfig['invoiceGenerationType'],
        vatNumber: values.vatCode,
      })
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      }
    } finally {
      setIsSaving(false)
    }
  }, [])

  const onCancel = useCallback(() => {
    reset()
  }, [reset])

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="rounded-3xl border border-monochrome-120 bg-gradient-4">
          <div className="p-[30px]">
            <h2 className="text-title-xs mb-[25px] text-monochrome-1">
              Financial Settings
            </h2>
            <form
              id="settings-financing-form"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="flex flex-col gap-[25px]">
                <FormGroup label="Generate Invoice">
                  <SelectField
                    outline
                    options={GENERATE_INVOICE_OPTIONS}
                    value={generateInvoiceValue}
                    onValueChange={(value) =>
                      setValue(
                        'generateInvoice',
                        value as
                          | 'OFF'
                          | 'ON_DEMAND_MANUAL'
                          | 'ON_DEMAND_AUTO'
                          | 'ALWAYS',
                      )
                    }
                    triggerClassName="!h-[44px] rounded-[16px]"
                    placeholder="Select Option"
                    disabled={!hasEditPermission}
                    error={errors.generateInvoice?.message}
                  />
                </FormGroup>
                <div className="grid grid-cols-3 gap-3">
                  <FormGroup label="Company Name">
                    <Input
                      isInvalid={!!errors.companyName?.message}
                      {...register('companyName')}
                      placeholder="Enter Company Name"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  <FormGroup label="CUI Number">
                    <Input
                      isInvalid={!!errors.companyIdNumber?.message}
                      {...register('companyIdNumber')}
                      placeholder="Enter CUI Number"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  <FormGroup label="VAT Code">
                    <Input
                      isInvalid={!!errors.vatCode?.message}
                      {...register('vatCode')}
                      placeholder="Enter VAT Code"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <FormGroup label="Country">
                    <SelectField
                      outline
                      options={map(getData(), (country) => ({
                        label: country.name,
                        value: country.name,
                      }))}
                      triggerClassName="!h-[44px] rounded-[16px]"
                      value={countryValue}
                      onValueChange={(value) => setValue('country', value)}
                      placeholder="Select Country"
                      disabled={!hasEditPermission}
                      error={errors.country?.message}
                    />
                  </FormGroup>
                  <FormGroup label="City">
                    <Input
                      isInvalid={!!errors.city?.message}
                      {...register('city')}
                      placeholder="Enter City"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  <FormGroup label="Company Address">
                    <Input
                      isInvalid={!!errors.companyAddress?.message}
                      {...register('companyAddress')}
                      placeholder="Enter Company Address"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                </div>
              </div>
            </form>
          </div>
          <div className="flex justify-end gap-2 p-3 border-t border-monochrome-120">
            <Button
              onClick={onCancel}
              variant="secondary"
              disabled={!hasEditPermission}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              form="settings-financing-form"
              type="submit"
              disabled={isSaving || !hasEditPermission}
              loading={isSaving}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
